import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { AppState } from 'src/state/store'

// LP / Corporate (Legal Person) questionnaire steps. Sections A-G of the LP
// build spec map 1:1 to steps 1-7; the per-person blocks (C3 UBOs, D1/D2
// management) live inside Ownership/Management. Completed kept at 10 to mirror
// the NP-Business slice (room to insert steps without renumbering).
export enum FintwinCorporateStepEnum {
  Company = 1, // Section A - the company
  BusinessActivity = 2, // Section B - business activity
  Ownership = 3, // Section C - ownership & control (+ C3 UBOs)
  Management = 4, // Section D - management & representative
  AccountUse = 5, // Section E - how the account will be used
  SourceOfFunds = 6, // Section F - source of funds & wealth
  // Optional supporting documents, before Section G. Declarations shifted
  // 7 -> 8 to make room (mirrors the individual fintwin flow).
  Documents = 7,
  Declarations = 8, // Section G - declarations & consent
  Completed = 10,
}

export interface FintwinCorporateVerificationState {
  currentStep: FintwinCorporateStepEnum
  availableStep: FintwinCorporateStepEnum
  isCompleted: boolean
  formValues: Record<string, unknown>
  // Id of the user this in-progress questionnaire belongs to. See
  // `claimCorporateOwner` and the mirror note in fintwinVerificationReducer.
  ownerId: number | null
}

export const initialFintwinCorporateVerificationState: FintwinCorporateVerificationState =
  {
    currentStep: FintwinCorporateStepEnum.Company,
    availableStep: FintwinCorporateStepEnum.Company,
    isCompleted: false,
    formValues: {},
    ownerId: null,
  }

const hydrate = createAction<AppState>(HYDRATE)

const fintwinCorporateVerificationSlice = createSlice({
  name: 'fintwinCorporateVerification',
  initialState: initialFintwinCorporateVerificationState,
  reducers: {
    changeCurrentStep(state, action: PayloadAction<FintwinCorporateStepEnum>) {
      state.currentStep = action.payload
    },
    changeAvailableStep(
      state,
      action: PayloadAction<FintwinCorporateStepEnum>
    ) {
      state.availableStep = action.payload
    },
    progressToStep(state, action: PayloadAction<FintwinCorporateStepEnum>) {
      const nextStep = action.payload

      state.currentStep = nextStep

      if (nextStep > state.availableStep) {
        state.availableStep = nextStep
      }
    },
    addFormValues(state, action: PayloadAction<Record<string, unknown>>) {
      state.formValues = { ...state.formValues, ...action.payload }
    },
    completeCorporateVerification(state) {
      state.isCompleted = true
      state.currentStep = FintwinCorporateStepEnum.Completed
      state.availableStep = FintwinCorporateStepEnum.Completed
    },
    // Mirror of claimFintwinOwner: same owner resumes, different/unknown owner
    // gets a clean questionnaire. Guards against cross-user bleed on logout,
    // which no longer wipes this slice.
    claimCorporateOwner(state, action: PayloadAction<number>) {
      if (state.ownerId !== action.payload) {
        return {
          ...initialFintwinCorporateVerificationState,
          ownerId: action.payload,
        }
      }
    },
    flushFintwinCorporate() {
      return initialFintwinCorporateVerificationState
    },
  },
  extraReducers: builder => {
    builder.addCase(hydrate, state => state)
  },
})

const { actions, reducer } = fintwinCorporateVerificationSlice

export const {
  changeCurrentStep,
  changeAvailableStep,
  progressToStep,
  addFormValues,
  completeCorporateVerification,
  claimCorporateOwner,
  flushFintwinCorporate,
} = actions

export default reducer
